import React, { MouseEvent, ReactNode, useCallback, useMemo } from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { faRoute, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Box,
  Checkbox,
  Group,
  Image,
  Skeleton,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import classes from "./Item.module.css";

const ImageIcon = ({ src }: { src: string }) =>
  src ? (
    <Image
      radius="xs"
      width={25}
      fit="contain"
      height={30}
      src={src}
      // Mantine breaks the UI here by adding a class with
      // width: 100% to the image, so we need to override it
      style={{ width: "auto" }}
    />
  ) : (
    <Skeleton width={20} height={20} circle />
  );

export type ItemProps = {
  id: number;
  index: number;
  name: string;
  onClick?: (e: MouseEvent, index: number) => void;
  onRightButtonClick: (id: number) => void;
  selected: boolean;
  hidden?: boolean;
  bottomInfo?: string;
  leftImageSrc?: string;
  leftIcon?: ReactNode;
  isSelectMode?: boolean;
  isMultiselected?: boolean;
  trackIconColor?: string;
};

export const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  (
    {
      id,
      index,
      name,
      onClick,
      onRightButtonClick,
      selected,
      hidden = false,
      bottomInfo,
      leftIcon,
      leftImageSrc,
      trackIconColor,
      isSelectMode,
      isMultiselected: isSelectModeSelected,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useShownColorScheme();
    const handleRightButtonClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onRightButtonClick(id);
      },
      [id, onRightButtonClick]
    );

    const handleClick = useCallback(
      (e: MouseEvent) => onClick?.(e, index),
      [index, onClick]
    );

    const LeftSideIcon = useMemo(
      () => (
        <Box style={{ userSelect: "none", opacity: hidden ? 0.7 : undefined }}>
          {leftIcon && leftIcon}
          {leftImageSrc && <ImageIcon src={leftImageSrc} />}
          {trackIconColor && (
            <FontAwesomeIcon
              icon={faRoute}
              color={trackIconColor}
              fontSize={20}
              // opacity={hidden ? 0.7 : undefined}
            />
          )}
        </Box>
      ),
      [hidden, leftIcon, leftImageSrc, trackIconColor]
    );

    return useMemo(
      () => (
        <Group
          ref={ref}
          onClick={handleClick}
          gap="sm"
          px={13}
          py={8}
          key={id}
          className={classes.item}
          mod={{ selected, "select-mode": isSelectMode }}
        >
          {LeftSideIcon}
          <Stack style={{ flexGrow: 1 }} gap={3}>
            <Text className={classes.name} mod={{ hidden }} lineClamp={2}>
              {name}
            </Text>
            {bottomInfo && (
              <Text size="xs" className={classes.info} mod={{ hidden }}>
                {bottomInfo}
              </Text>
            )}
          </Stack>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexGrow: 1,
              width: 28,
              height: 28,
            }}
          >
            {isSelectMode ? (
              <div style={{ padding: "5px" }}>
                <Checkbox
                  checked={isSelectModeSelected}
                  onChange={undefined}
                  readOnly
                />
              </div>
            ) : (
              <ActionIcon
                onClick={handleRightButtonClick}
                color={
                  hidden
                    ? "gray"
                    : colorScheme === "dark"
                      ? theme.colors.violet[2]
                      : theme.colors.violet[7]
                }
                variant="transparent"
              >
                <FontAwesomeIcon
                  opacity={hidden ? 0.7 : undefined}
                  icon={hidden ? faEyeSlash : faEye}
                />
              </ActionIcon>
            )}
          </div>
        </Group>
      ),
      [
        colorScheme,
        ref,
        handleClick,
        id,
        selected,
        hidden,
        isSelectMode,
        LeftSideIcon,
        name,
        bottomInfo,
        isSelectModeSelected,
        handleRightButtonClick,
        theme.colors.violet,
      ]
    );
  }
);
Item.displayName = "Item";
